import { Blob } from '@/services/types/blob-types';

/**
 * Patterns for matching driver's license documents
 */
const DRIVER_LICENSE_PATTERNS = {
  // Matches: driver's license, drivers license, driver license, driving license
  primary: /driver['']?s?\s+licen[cs]e|driving\s+licen[cs]e/i,
};

/**
 * Client name information for matching
 */
export interface ClientNameInfo {
  firstName?: string;
  lastName?: string;
}

/**
 * Result of document matching with score
 */
interface ScoredDocument {
  blob: Blob;
  score: number;
  matchReason: string;
}

/**
 * Extracts client name information from matter client data
 */
export function extractClientNames(
  client:
    | {
        first_name?: string;
        last_name?: string;
        name?: string;
      }
    | undefined,
): ClientNameInfo | null {
  if (!client) return null;

  return {
    firstName: client.first_name || client.name?.split(' ')[0],
    lastName: client.last_name || client.name?.split(' ').slice(-1)[0],
  };
}

/**
 * Checks if a document name contains the client's name
 */
function documentContainsClientName(
  documentName: string,
  clientNames: ClientNameInfo,
): { matches: boolean; score: number; nameMatch: string } {
  const lowerName = documentName.toLowerCase();
  let score = 0;
  const matches: string[] = [];

  if (
    clientNames.firstName &&
    lowerName.includes(clientNames.firstName.toLowerCase())
  ) {
    score += 1;
    matches.push(`first name "${clientNames.firstName}"`);
  }

  if (
    clientNames.lastName &&
    lowerName.includes(clientNames.lastName.toLowerCase())
  ) {
    score += 1;
    matches.push(`last name "${clientNames.lastName}"`);
  }

  const nameMatch =
    matches.length === 2
      ? 'both client names'
      : matches.length === 1
        ? `client's ${matches[0]}`
        : '';

  return {
    matches: score > 0,
    score,
    nameMatch,
  };
}

/**
 * Checks if a document is a driver's license
 */
function isDriversLicense(documentName: string): boolean {
  return DRIVER_LICENSE_PATTERNS.primary.test(documentName);
}

/**
 * Finds driver's license documents that match the client's name
 */
export function findMatchingDriversLicense(
  blobs: Blob[],
  clientNames: ClientNameInfo | null,
): ScoredDocument | null {
  if (!clientNames || blobs.length === 0) {
    return null;
  }

  // Filter documents that match driver's license pattern and contain client name
  const matches: ScoredDocument[] = blobs
    .filter((blob) => {
      if (!isDriversLicense(blob.name)) {
        return false;
      }

      const { matches: hasMatch } = documentContainsClientName(
        blob.name,
        clientNames,
      );
      return hasMatch;
    })
    .map((blob) => {
      const { score, nameMatch } = documentContainsClientName(
        blob.name,
        clientNames,
      );
      return {
        blob,
        score,
        matchReason: `Contains driver's license and ${nameMatch}`,
      };
    });

  if (matches.length === 0) {
    return null;
  }

  // Sort by score (desc) then by modified date (desc)
  matches.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return (
      new Date(b.blob.modified_at).getTime() -
      new Date(a.blob.modified_at).getTime()
    );
  });

  return matches[0];
}

/**
 * Creates a human-readable message for auto-selection
 */
export function createAutoSelectionMessage(
  documentName: string,
  matchReason: string,
): string {
  return `Auto-selected "${documentName}" - ${matchReason.toLowerCase()}`;
}
